import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CalendarIcon, Clock, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { GutterOfferBanner } from '@/components/GutterOfferBanner';

const TIME_SLOTS = [
  '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'
];

export default function GutterCleaning() {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState<Date[]>([]);
  const [blockedSlots, setBlockedSlots] = useState<string[]>([]);

  useEffect(() => {
    fetchBlockedDates();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchBlockedTimeslots(selectedDate);
    }
  }, [selectedDate]);

  const fetchBlockedDates = async () => {
    try {
      const { data, error } = await supabase
        .from('gutter_cleaning_availability')
        .select('date')
        .eq('is_available', false);

      if (error) throw error;

      const dates = (data || []).map(record => new Date(record.date));
      setBlockedDates(dates);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const fetchBlockedTimeslots = async (date: Date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const { data, error } = await supabase
        .from('gutter_blocked_timeslots')
        .select('blocked_time')
        .eq('booking_date', dateStr);

      if (error) throw error;

      const blocked = (data || []).map(record => record.blocked_time);
      setBlockedSlots(blocked);
    } catch (error) {
      console.error('Error fetching blocked timeslots:', error);
      setBlockedSlots([]);
    }
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime || !address.trim() || !phone.trim() || !email.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      console.log('=== BOOKING DEBUG ===');
      console.log('Original selectedDate:', selectedDate);
      console.log('Formatted date:', formattedDate);
      console.log('Date format test:', /^\d{4}-\d{2}-\d{2}$/.test(formattedDate));
      console.log('Payload:', {
        date: formattedDate,
        time: selectedTime,
        address: address.trim(),
        phone: phone.trim(),
        email: email.trim(),
      });
      
      const { data, error } = await supabase.functions.invoke('send-gutter-booking-email', {
        body: {
          date: formattedDate,
          time: selectedTime,
          address: address.trim(),
          phone: phone.trim(),
          email: email.trim(),
          notes: notes.trim() || undefined,
        },
      });

      if (error) {
        console.error('Error sending booking email:', error);
        throw error;
      }

      console.log('Booking email sent successfully:', data);
      
      toast.success(
        `Booking request sent! You'll receive a confirmation email at ${email}`,
        { duration: 5000 }
      );
      
      // Reset form
      setSelectedDate(undefined);
      setSelectedTime('');
      setAddress('');
      setPhone('');
      setEmail('');
      setNotes('');
    } catch (error: any) {
      console.error('Failed to send booking:', error);
      toast.error(
        error.message || 'Failed to send booking request. Please try again or contact us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Professional Gutter Cleaning
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Keep your gutters clean and prevent costly water damage. Book your service today!
          </p>
        </div>

        {/* Promotional Banner */}
        <div className="max-w-6xl mx-auto">
          <GutterOfferBanner />
        </div>

        {/* Booking Section */}
        <div className="max-w-6xl mx-auto mb-12">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <CalendarIcon className="h-6 w-6" />
                Book Your Gutter Cleaning
              </CardTitle>
              <CardDescription>
                Choose your preferred date and time
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Calendar Section */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Select Date *</Label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => {
                        // Disable past dates
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;
                        
                        // Disable Sundays
                        if (date.getDay() === 0) return true;
                        
                        // Disable blocked dates from admin
                        if (!blockedDates || blockedDates.length === 0) return false;
                        
                        return blockedDates.some(
                          blockedDate => 
                            blockedDate.getFullYear() === date.getFullYear() &&
                            blockedDate.getMonth() === date.getMonth() &&
                            blockedDate.getDate() === date.getDate()
                        );
                      }}
                      className="rounded-md border"
                    />
                    {selectedDate && (
                      <p className="mt-3 text-sm text-muted-foreground">
                        Selected: {format(selectedDate, 'PPP')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Booking Details Section */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="time-slot" className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Time Slot *
                    </Label>
                    <Select value={selectedTime} onValueChange={setSelectedTime}>
                      <SelectTrigger id="time-slot">
                        <SelectValue placeholder="Choose a time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((time) => {
                          const isBlocked = blockedSlots.includes(time);
                          return (
                            <SelectItem 
                              key={time} 
                              value={time}
                              disabled={isBlocked}
                            >
                              {time} {isBlocked && '(Unavailable)'}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Property Address *</Label>
                    <Input
                      id="address"
                      placeholder="Enter your property address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any special requirements or access instructions..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Info */}
              <Card className="bg-muted/50">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-lg">Service Details</h4>
                    <div className="grid md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                      <div>
                        <p className="font-medium text-foreground mb-2">What's Included:</p>
                        <ul className="space-y-1">
                          <li>• Complete debris removal</li>
                          <li>• Before & after photos</li>
                          <li>• Quality guarantee</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium text-foreground mb-2">Pricing:</p>
                        <ul className="space-y-1">
                          <li>• Standard residential: £80</li>
                          <li>• Average time: 1-2 hours</li>
                          <li>• Payment on completion</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Book Button */}
              <Button 
                onClick={handleBooking}
                disabled={isSubmitting || !selectedDate || !selectedTime || !address.trim() || !phone.trim() || !email.trim()}
                className="w-full md:w-auto"
                size="lg"
              >
                {isSubmitting ? 'Booking...' : 'Book Appointment'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Prevent Water Damage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Clean gutters prevent water overflow that can damage your roof, walls, and foundation.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Extend Gutter Life
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Regular cleaning prevents rust and corrosion, extending the lifespan of your gutters.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Pest Prevention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Remove debris that attracts insects, birds, and rodents looking for nesting spots.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Why Choose Us */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl">Why Choose Our Gutter Cleaning Service?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3">Professional Service</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>✓ Experienced professionals</li>
                  <li>✓ Fully insured service</li>
                  <li>✓ Safety-first approach</li>
                  <li>✓ Latest equipment</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Customer Satisfaction</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>✓ Satisfaction guaranteed</li>
                  <li>✓ Competitive pricing</li>
                  <li>✓ No hidden fees</li>
                  <li>✓ Free quotes</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
